export const EMPTY_WEEK_FILTERS = { search: '', projectId: '', workerId: '' };
export function localDateStr(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function dayStr(d) {
    if (!d)
        return '';
    return typeof d === 'string' ? d.slice(0, 10) : localDateStr(d);
}
// Completed/sick/holiday deployments still count for the days they actually
// covered — otherwise moving or closing a deployment blanks the worker's
// history. Cancelled ones never happened.
export function deployCoversDay(d, dateStr, todayStr) {
    if (d.status === 'cancelled')
        return false;
    if (dayStr(d.startDate) > dateStr)
        return false;
    const end = d.endDate ? dayStr(d.endDate) : null;
    if (d.status === 'active')
        return !end || end >= dateStr;
    return dateStr <= (end ?? todayStr);
}
export function weekStartFor(date) {
    const d = typeof date === 'string' ? new Date(`${date.slice(0, 10)}T00:00:00`) : new Date(date);
    const dow = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - dow);
    return localDateStr(d);
}
export function weekDates(weekStart) {
    const base = new Date(`${weekStart}T00:00:00`);
    return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(base);
        d.setDate(base.getDate() + i);
        return localDateStr(d);
    });
}
export const WEEK_DAY_LABELS = ['MON', 'TUE', 'WED', 'THURS', 'FRI', 'SAT', 'SUN'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
export function weekEndingLabel(weekStart) {
    const friday = weekDates(weekStart)[4];
    const d = new Date(`${friday}T00:00:00`);
    return `${String(d.getDate()).padStart(2, '0')}-${MONTHS_SHORT[d.getMonth()]}-${d.getFullYear()}`;
}
export function weekFilterSummary(f, names) {
    const parts = [];
    if (f.search.trim())
        parts.push(`Search "${f.search.trim()}"`);
    if (f.projectId)
        parts.push(`Project: ${names?.project ?? f.projectId}`);
    if (f.workerId)
        parts.push(`Worker: ${names?.worker ?? f.workerId}`);
    return parts.length ? parts.join(' · ') : 'None';
}
export function buildDeploymentWeekModel(inputs, descriptor, todayStr = localDateStr(new Date())) {
    const weekStart = weekStartFor(descriptor.weekStart);
    const dates = weekDates(weekStart);
    const f = descriptor.filters;
    // Weekday deployments must not bleed into Sat/Sun — weekend work is the
    // exception, recorded as a deployment anchored in that weekend.
    const satStr = dates[5];
    const sunStr = dates[6];
    const inWeekend = (v) => {
        if (!v)
            return false;
        const s = dayStr(v);
        return s >= satStr && s <= sunStr;
    };
    const coversGridDay = (d, ds) => {
        if (!deployCoversDay(d, ds, todayStr))
            return false;
        if (ds < satStr)
            return true;
        return inWeekend(d.startDate) || inWeekend(d.endDate);
    };
    const deploysByWorker = new Map();
    for (const d of inputs.deployments) {
        if (d.status === 'cancelled')
            continue;
        const arr = deploysByWorker.get(d.workerId) ?? [];
        arr.push(d);
        deploysByWorker.set(d.workerId, arr);
    }
    const absencesByWorker = new Map();
    for (const a of inputs.absences) {
        const arr = absencesByWorker.get(a.workerId) ?? [];
        arr.push(a);
        absencesByWorker.set(a.workerId, arr);
    }
    const rows = [];
    for (const w of inputs.workers) {
        if (f.workerId && w.id !== f.workerId)
            continue;
        const s = f.search.toLowerCase().trim();
        if (s) {
            const hay = [w.name, w.role, w.agencyCode ?? '', w.companyRef ?? ''].join(' ').toLowerCase();
            if (!hay.includes(s))
                continue;
        }
        const deploys = deploysByWorker.get(w.id) ?? [];
        if (f.projectId && !deploys.some((d) => d.projectId === f.projectId && dates.some((ds) => coversGridDay(d, ds))))
            continue;
        const workerAbsences = absencesByWorker.get(w.id) ?? [];
        const cells = dates.map((ds) => {
            const covering = deploys.filter((d) => coversGridDay(d, ds));
            const codes = [];
            const deploymentIds = [];
            for (const d of covering) {
                const code = d.projectCode ?? d.projectName ?? d.projectId;
                if (!codes.includes(code)) {
                    codes.push(code);
                    deploymentIds.push(d.id);
                }
            }
            const absence = workerAbsences.find((a) => dayStr(a.startDate) <= ds && dayStr(a.endDate) >= ds);
            return { codes, deploymentIds, absence: absence?.type ?? null };
        });
        if (cells.every((c) => c.codes.length === 0 && !c.absence))
            continue;
        rows.push({
            workerId: w.id,
            name: w.name,
            trade: w.role,
            agency: w.agencyCode ?? 'Direct',
            companyRef: w.companyRef ?? '',
            cells,
        });
    }
    const byAgency = new Map();
    for (const r of rows) {
        const arr = byAgency.get(r.agency) ?? [];
        arr.push(r);
        byAgency.set(r.agency, arr);
    }
    const groups = [...byAgency.entries()]
        .sort(([a], [b]) => {
        if (a === 'Direct')
            return 1;
        if (b === 'Direct')
            return -1;
        return a.localeCompare(b);
    })
        .map(([agency, groupRows]) => ({ agency, rows: groupRows.sort((a, b) => a.name.localeCompare(b.name)) }));
    return {
        weekStart,
        dates,
        weekEnding: weekEndingLabel(weekStart),
        groups,
        totalRows: rows.length,
        filterSummary: weekFilterSummary(f),
    };
}
//# sourceMappingURL=deployment-week.js.map